/**
 * Stage 2b: Competitor Plan — context-based version using StagePanel.
 *
 * Normalizes competitor URLs from Stage 2a SERP into an extraction plan.
 * Non-LLM stage — just transforms data.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function CompetitorUrlsInput() {
  const { document } = useArticleContext();
  const urls = document?.targeting?.competitors?.urls || [];
  return (
    <div>
      <div className="text-xs text-gray-500 mb-1">Stage 2a competitor URLs (read-only)</div>
      <textarea
        className="w-full border rounded px-2 py-1 text-xs bg-gray-50"
        rows={6}
        value={urls.length > 0
          ? JSON.stringify({ competitor_urls: urls }, null, 2)
          : 'No Stage 2a data available.'}
        disabled
      />
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const urls = doc.targeting?.competitors?.urls || [];
  // After 2b runs, urls have id and status fields
  const hasIds = urls.length > 0 && urls[0].id;
  if (!hasIds) {
    return <div className="text-sm text-gray-500">No competitor plan yet.</div>;
  }
  return (
    <div>
      <div className="text-xs font-medium text-gray-700 mb-1">Competitor plan</div>
      <div className="space-y-2">
        {urls.map((entry) => (
          <div key={entry.id || entry.url} className="border rounded p-2 text-xs border-gray-200">
            <div className="font-medium text-gray-800">{entry.id} • {entry.domain || entry.url}</div>
            <div className="text-gray-600">{entry.url}</div>
            {entry.position != null && (
              <div className="text-gray-500">SERP position: {entry.position}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Stage2bCompetitorPlanV2() {
  const { document } = useArticleContext();
  const hasUrls = (document?.targeting?.competitors?.urls || []).length > 0;

  return (
    <StagePanel
      stageId="stage_2b_competitor_plan"
      title="Stage 2b: Competitor Plan"
      description="Builds extraction plan from Stage 2a competitor URLs."
      runDisabled={!hasUrls}
      runDisabledReason={!hasUrls ? 'Requires Stage 2a competitor URLs' : undefined}
      renderOutput={renderOutput}
    >
      <CompetitorUrlsInput />
    </StagePanel>
  );
}
